/**
 * A utility for caching and deduplicating in-flight promises.
 */
class PromiseCache {
    constructor() {
        this.cache = {};
    }

    /**
     * Check if a request with the given key is already in flight
     *
     * @param {string} key - The cache key to check
     * @returns {boolean} - Whether the key exists in the cache
     */
    has(key) {
        return !!this.cache[key];
    }

    /**
     * Get a promise from the cache
     *
     * @param {string} key - The cache key
     * @returns {Promise|null} - The cached promise or null if it doesn't exist
     */
    get(key) {
        return this.cache[key] || null;
    }

    /**
     * Memoize a promise-returning function to prevent duplicate executions
     *
     * @param {string} key - The unique cache key that identifies this request
     * @param {Function} promiseFactory - Function that returns a promise
     * @returns {Promise} - Either the cached promise or the newly created promise
     */
    memoize(key, promiseFactory) {
        if (this.has(key)) {
            return this.get(key);
        }

        // Create the promise using the factory function
        const promise = promiseFactory();

        // Store it in the cache
        this.cache[key] = promise;

        // Clean up the cache entry when the promise settles
        promise.finally(() => {
            delete this.cache[key];
        });

        return promise;
    }

    /**
     * Clear the entire cache or a specific key
     *
     * @param {string} [key] - Optional key to clear. If not provided, clears the entire cache.
     */
    clear(key) {
        if (this.has(key)) {
            delete this.cache[key];
        }
    }
}

/**
 * Create a singleton instance for global use
 */
export const promiseCache = new PromiseCache();
